<template>
  <WizardModal
    v-model="currentStep"
    v-model:show-modal="isVisible"
    :steps="wizardSteps"
    :modal-title="t('Adicionar serviço ao orçamento')"
    elem-id="wizard_add_servico"
    size="compactDesktop"
    custom-buttons
    :next-label="nextButtonLabel"
    :next-icon="nextButtonIcon"
    @finish="handleSave"
    @cancel="handleCancel"
    @close="handleCancel"
  >
    <!-- Step 0: Select Categoria -->
    <template #step-0>
      <div class="step-container">
        <h3 class="step-title">{{ t('Selecione a categoria do serviço') }}</h3>
        <buttons-list
          data-testid="orcamento-wizard-categoria-list"
          elem-id="wizard_select_categoria"
          :itens-select="categoriasArray"
          v-model="selectedCategoriaId"
          :ng-required="true"
          :validation="() => !!selectedCategoriaId"
        />
      </div>
    </template>

    <!-- Step 1: Select Servico -->
    <template #step-1>
      <div class="step-container">
        <h3 class="step-title">{{ t('Selecione o serviço') }}</h3>
        <p class="step-subtitle">{{ t('Categoria') }}: <strong>{{ selectedCategoriaNome }}</strong></p>
        <div class="servico-list-wrapper">
          <buttons-list
            data-testid="orcamento-wizard-servico-list"
            elem-id="wizard_select_servico"
            :label="t('Serviços')"
            :itens-select="servicosArray"
            v-model="selectedServicoId"
            :ng-required="true"
            :validation="() => !!selectedServicoId"
            :searchEnabled="true"
          />
        </div>
      </div>
    </template>

    <!-- Step 2: Set Quantity & Value -->
    <template #step-2>
      <div class="step-container">
        <h3 class="step-title">{{ t('Defina a quantidade e valor') }}</h3>
        <div class="step-info">
          <p>{{ t('Categoria') }}: <strong>{{ selectedCategoriaNome }}</strong></p>
          <p>{{ t('Serviço') }}: <strong>{{ selectedServicoNome }}</strong></p>
        </div>

        <div class="form-row">
          <input-number
            data-testid="orcamento-wizard-servico-qtd-input"
            elem-id="wizard_servico_qtd"
            :label="t('Quantidade')"
            v-model="quantity"
            :placeholder="t('Quantidade')"
            :ng-required="true"
            :validation="() => quantity > 0"
          />
          <input-currency
            data-testid="orcamento-wizard-servico-valor-input"
            elem-id="wizard_servico_valor"
            :label="currencySymbol"
            v-model="value"
            @update:model-value="valorDigitado = true"
            :placeholder="t('Valor total')"
            :ng-required="true"
            :validation="() => value >= 0"
          />
        </div>

        <div class="total-preview">
          <strong>{{ t('Total') }}:</strong> {{ n(value, 'currency') }}
        </div>
      </div>
    </template>
  </WizardModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardModal from '../../ui/WizardModal.vue';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';

type Id = number | string;

interface Servico {
  servico_id: Id;
  servico_nome: string;
  servico_valor_padrao?: number;
  [key: string]: unknown;
}

interface Categoria {
  servicoscategoria_id: Id;
  servicoscategoria_nome: string;
  servicos: Servico[];
  [key: string]: unknown;
}

interface CategoriasMap {
  [key: string]: Categoria;
}

interface SelectItem {
  ID: Id;
  Name: string;
}

interface WizardStep {
  title: string;
  validate: () => boolean;
  onEnter?: () => void;
}

interface ModalOpenPayload {
  categorias?: CategoriasMap;
}

const { t, n } = useI18n();

// Props
const props = withDefaults(
  defineProps<{
    categorias?: CategoriasMap;
    currencySymbol?: string;
  }>(),
  {
    categorias: () => ({}),
    currencySymbol: 'R$',
  }
);

// Emits - only close for local event handling, service-added goes via eventBus
const emit = defineEmits<{
  (e: 'close'): void;
}>();

// State
const isVisible = ref<boolean>(false);
const currentStep = ref<number>(0);
const selectedCategoriaId = ref<Id | ''>('');
const selectedServicoId = ref<Id | ''>('');
const quantity = ref<number>(1);
const value = ref<number>(0);
const currentCategorias = ref<CategoriasMap | null>(null);
// servico_valor é o TOTAL da linha: sugere padrão × quantidade até a pessoa digitar um valor.
const valorDigitado = ref<boolean>(false);

const sugerirValor = (): void => {
  if (selectedServico.value && !valorDigitado.value) {
    value.value = (selectedServico.value.servico_valor_padrao || 0) * quantity.value;
  }
};

// Event handlers for centralized ModalManager
const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('OrcamentoAddServicoWizard', 'handleModalOpen', 'Modal open event received', data);
  if (data?.categorias) {
    currentCategorias.value = data.categorias;
  }
  open();
};

// Wizard steps definition with validation
const wizardSteps = computed<WizardStep[]>(() => [
  {
    title: t('Categoria'),
    validate: () => !!selectedCategoriaId.value
  },
  {
    title: t('Serviço'),
    validate: () => !!selectedServicoId.value,
    onEnter: () => {
      // Reset servico selection when entering this step
      if (!selectedServicoId.value) {
        selectedServicoId.value = '';
      }
    }
  },
  {
    title: t('Quantidade e Valor total'),
    validate: () => quantity.value > 0 && value.value >= 0,
    onEnter: () => sugerirValor()
  }
]);

// Computed - use internal state or fallback to props
const categorias = computed<CategoriasMap>(() => currentCategorias.value || props.categorias);

// Convert categorias object to array for ButtonsList
const categoriasArray = computed<SelectItem[]>(() => {
  return Object.values(categorias.value || {}).map(cat => ({
    ID: cat.servicoscategoria_id,
    Name: cat.servicoscategoria_nome
  }));
});

// Get selected categoria object
const selectedCategoria = computed<Categoria | null>(() => {
  if (!selectedCategoriaId.value) return null;
  return categorias.value[String(selectedCategoriaId.value)] || null;
});

// Get categoria name
const selectedCategoriaNome = computed<string>(() => {
  return selectedCategoria.value?.servicoscategoria_nome || '';
});

// Convert servicos to array for ButtonsList
const servicosArray = computed<SelectItem[]>(() => {
  if (!selectedCategoria.value) return [];
  return selectedCategoria.value.servicos.map(serv => ({
    ID: serv.servico_id,
    Name: serv.servico_nome
  }));
});

// Get selected servico object
const selectedServico = computed<Servico | null>(() => {
  if (!selectedCategoria.value || !selectedServicoId.value) return null;
  return selectedCategoria.value.servicos.find(
    s => s.servico_id === selectedServicoId.value
  ) || null;
});

// Get servico name
const selectedServicoNome = computed<string>(() => {
  return selectedServico.value?.servico_nome || '';
});

// Custom button labels
const nextButtonLabel = computed<(step: number) => string>(() => {
  return (step: number) => {
    if (step === 0 || step === 1) {
      return t('Próximo');
    } else if (step === 2) {
      return t('Adicionar serviço');
    }
    return t('Próximo');
  };
});

const nextButtonIcon = computed<(step: number) => string>(() => {
  return (step: number) => {
    if (step === 0 || step === 1) {
      return 'pi pi-arrow-right';
    } else if (step === 2) {
      return 'pi pi-check';
    }
    return 'pi pi-arrow-right';
  };
});

// Methods
const open = (): void => {
  log('OrcamentoAddServicoWizard', 'open', 'Opening wizard');
  isVisible.value = true;
  resetWizard();
};

const handleCancel = (): void => {
  log('OrcamentoAddServicoWizard', 'handleCancel', 'Canceling wizard');
  isVisible.value = false;
  currentCategorias.value = null;
  emit('close');
  resetWizard();
};

const resetWizard = (): void => {
  log('OrcamentoAddServicoWizard', 'resetWizard', 'Resetting wizard state');
  currentStep.value = 0;
  selectedCategoriaId.value = '';
  selectedServicoId.value = '';
  quantity.value = 1;
  value.value = 0;
  valorDigitado.value = false;
};

// Lifecycle hooks for centralized ModalManager
onMounted(() => {
  log('OrcamentoAddServicoWizard', 'onMounted', 'Component mounted');
  eventBus.on('modal.orcamento-add-servico.open', handleModalOpen);
  eventBus.on('modal.orcamento-add-servico.close', handleCancel);
});

onBeforeUnmount(() => {
  log('OrcamentoAddServicoWizard', 'onBeforeUnmount', 'Component unmounting');
  eventBus.off('modal.orcamento-add-servico.open', handleModalOpen);
  eventBus.off('modal.orcamento-add-servico.close', handleCancel);
});

const handleSave = (): void => {
  log('OrcamentoAddServicoWizard', 'handleSave', 'Saving service', {
    categoria: selectedCategoriaId.value,
    servico: selectedServicoId.value,
    quantity: quantity.value,
    value: value.value
  });

  // Validate before saving
  if (!selectedCategoriaId.value || !selectedServicoId.value || quantity.value <= 0 || value.value < 0) {
    log('OrcamentoAddServicoWizard', 'handleSave', 'Validation failed - cannot save');
    return;
  }

  // Create service object
  const servicoData = {
    servico_id: selectedServicoId.value,
    valor: value.value,
    qtd: quantity.value,
    categoria_id: selectedCategoriaId.value,
    categoria_nome: selectedCategoriaNome.value,
    uuid: `servico_${Date.now()}_${Math.random()}`
  };

  log('OrcamentoAddServicoWizard', 'handleSave', 'Emitting service-added via eventBus', servicoData);

  // Emit event via eventBus for centralized ModalManager pattern
  eventBus.emit('orcamento-add-servico.service-added', servicoData);

  // Close modal
  isVisible.value = false;
  resetWizard();
};

// Watch for categoria change to reset servico
watch(selectedCategoriaId, (newVal, oldVal) => {
  if (newVal !== oldVal && oldVal !== '') {
    log('OrcamentoAddServicoWizard', 'watch:selectedCategoriaId', 'Categoria changed, resetting servico');
    selectedServicoId.value = '';
  }
});

watch(quantity, sugerirValor);

// Outro serviço: o valor digitado era do anterior.
watch(selectedServicoId, () => {
  valorDigitado.value = false;
});

// Note: open() is now called via eventBus 'modal.orcamento-add-servico.open'
// No need for defineExpose in centralized ModalManager pattern
</script>

<style scoped>
.step-container {
  padding: 20px;
  max-width: 100%;
  overflow-x: hidden;
}

.step-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
}

.step-subtitle {
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  text-align: center;
}

.step-info {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 20px;
}

.step-info p {
  margin: 5px 0;
  font-size: 14px;
  color: #666;
}

.step-info strong {
  color: #333;
}

/* Form Row */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

/* Total Preview */
.total-preview {
  background-color: color-mix(in srgb, var(--app-primary-color) 10%, white);
  border: 2px solid var(--app-primary-color);
  border-radius: 6px;
  padding: 15px;
  text-align: right;
  font-size: 18px;
  margin-bottom: 20px;
  color: var(--app-primary-active-color);
}

/* Servico List Wrapper */
.servico-list-wrapper {
  width: 100%;
}

.servico-list-wrapper :deep(.button-list-container) {
  display: block !important;
}

.servico-list-wrapper :deep(.button-list-label) {
  display: none !important;
}

.servico-list-wrapper :deep(.button-list-content) {
  width: 100% !important;
}

/* Responsive */
@media (max-width: 768px) {
  .step-container {
    padding: 10px;
  }

  .step-title {
    font-size: 16px;
  }
}
</style>
